import globalStore from '@/services/global.state';
import {errorLog} from '@/utils';
import {goTo} from '@/utils/route';
import {
  FissionV4Open,
  FissionV4Progress,
  getFissionV4Progress,
} from './fission-v4.service';
import {useFissionV4AvailabilityStore} from './fission-v4-availability.store';
import {openReferEarnEntryCashGift} from './home/refer-earn-wheel-card/entry-cash-gift-modal';
import {setReferEarnEntryCashGiftTransitionActive} from './refer-earn-entry-transition';

let pendingEntryProgress: FissionV4Progress | undefined;
let pendingEntryCashGiftFlow = false;
let pendingEntryCashGiftAmountIncrease = false;
let openingReferEarnInvitation: Promise<void> | undefined;
let referEarnPageNavigationDepth = 0;
let resolvePendingInvitationReady: (() => void) | undefined;

const REFER_EARN_ROUTE_NAME = 'ReferEarn';
const REFER_EARN_ROUTE_PATH = 'refer-earn';
const REFER_EARN_TAB_ENTRY_PATH = 'index/referral';

export const notifyReferEarnInvitationReady = () => {
  resolvePendingInvitationReady?.();
};

export const isReferEarnInvitationRoute = (routeOrUri?: string) => {
  const nextRouteOrUri = routeOrUri?.trim();
  if (!nextRouteOrUri) {
    return false;
  }

  let routeName = nextRouteOrUri;
  if (nextRouteOrUri.startsWith('route:')) {
    const routePayload = nextRouteOrUri.slice('route:'.length).trim();
    try {
      routeName = JSON.parse(routePayload)?.routeName || '';
    } catch {
      routeName = routePayload;
    }
  }

  const normalizedRouteName = routeName
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();

  return (
    normalizedRouteName === REFER_EARN_ROUTE_NAME.toLowerCase() ||
    normalizedRouteName === REFER_EARN_ROUTE_PATH ||
    normalizedRouteName === REFER_EARN_TAB_ENTRY_PATH ||
    normalizedRouteName === 'invite'
  );
};

const getNormalizedBrowserPath = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
};

const runReferEarnPageNavigation = (navigate: () => void) => {
  referEarnPageNavigationDepth += 1;
  try {
    navigate();
  } finally {
    referEarnPageNavigationDepth -= 1;
  }
};

const goToReferEarnPage = () => {
  runReferEarnPageNavigation(() => {
    goTo(REFER_EARN_ROUTE_NAME);
  });
};

const isReferEarnPageRoute = (routeOrUri?: string) => {
  const nextRouteOrUri = routeOrUri?.trim();
  if (!nextRouteOrUri) {
    return false;
  }

  let routeName = nextRouteOrUri;
  if (nextRouteOrUri.startsWith('route:')) {
    const routePayload = nextRouteOrUri.slice('route:'.length).trim();
    try {
      routeName = JSON.parse(routePayload)?.routeName || '';
    } catch {
      routeName = routePayload;
    }
  }

  const normalizedRouteName = routeName
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();

  return (
    normalizedRouteName === REFER_EARN_ROUTE_NAME.toLowerCase() ||
    normalizedRouteName === REFER_EARN_ROUTE_PATH ||
    normalizedRouteName === REFER_EARN_TAB_ENTRY_PATH
  );
};

export const openReferEarnInvitationIfNeeded = (routeOrUri?: string) => {
  if (referEarnPageNavigationDepth > 0) {
    return false;
  }
  // 只拦截 ReferEarn。route:Invite 配置后会误伤，不走礼物拦截。
  if (!isReferEarnPageRoute(routeOrUri)) {
    return false;
  }

  openReferEarnInvitation();
  return true;
};

const scheduleReferEarnPageNavigation = () => {
  goToReferEarnPage();

  if (typeof window === 'undefined') {
    return;
  }

  window.setTimeout(goToReferEarnPage, 0);
  window.setTimeout(() => {
    if (getNormalizedBrowserPath() === REFER_EARN_TAB_ENTRY_PATH) {
      goToReferEarnPage();
    }
  }, 240);
};

export const consumeReferEarnEntryProgress = () => {
  const progress = pendingEntryProgress;
  pendingEntryProgress = undefined;
  return progress;
};

export const consumeReferEarnEntryCashGiftFlow = () => {
  const pending = pendingEntryCashGiftFlow;
  pendingEntryCashGiftFlow = false;
  return pending;
};

export const hasPendingReferEarnEntryCashGiftAmountIncrease = () =>
  pendingEntryCashGiftAmountIncrease;

export const consumeReferEarnEntryCashGiftAmountIncrease = () => {
  const pending = pendingEntryCashGiftAmountIncrease;
  pendingEntryCashGiftAmountIncrease = false;
  return pending;
};

const waitForReferEarnInvitationReady = () =>
  new Promise<void>(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(fallbackTimer);
      if (resolvePendingInvitationReady === finish) {
        resolvePendingInvitationReady = undefined;
      }
      resolve();
    };
    const fallbackTimer = window.setTimeout(finish, 2000);

    resolvePendingInvitationReady?.();
    resolvePendingInvitationReady = finish;
  });

const prepareEntryCashGiftNavigation = (nextRound: FissionV4Open) => {
  pendingEntryProgress = {
    ...nextRound,
    status: 'IN_PROGRESS',
    currentAmount: 0,
  };
  pendingEntryCashGiftFlow = true;
  pendingEntryCashGiftAmountIncrease = true;
  setReferEarnEntryCashGiftTransitionActive(true);
  const invitationReady = waitForReferEarnInvitationReady();
  scheduleReferEarnPageNavigation();
  return invitationReady;
};

const openReferEarnInvitationFlow = async () => {
  if (!globalStore.token) {
    pendingEntryProgress = undefined;
    pendingEntryCashGiftFlow = false;
    pendingEntryCashGiftAmountIncrease = false;
    setReferEarnEntryCashGiftTransitionActive(false);
    // 未登录不请求 progress，按关闭带入页面
    useFissionV4AvailabilityStore.getState().resetWheelAvailability();
    goToReferEarnPage();
    return;
  }

  let progress: FissionV4Progress | undefined;
  globalStore.globalLoading.next(true);
  try {
    progress = await getFissionV4Progress();
  } catch (error) {
    errorLog('refer earn entry progress check failed', error);
  } finally {
    globalStore.globalLoading.next(false);
  }

  if (progress?.status === 'WAIT_OPEN') {
    try {
      await openReferEarnEntryCashGift(
        progress,
        prepareEntryCashGiftNavigation,
      );
    } finally {
      pendingEntryCashGiftFlow = false;
      setReferEarnEntryCashGiftTransitionActive(false);
    }
    return;
  }

  pendingEntryProgress = progress;
  pendingEntryCashGiftFlow = false;
  pendingEntryCashGiftAmountIncrease = false;
  setReferEarnEntryCashGiftTransitionActive(false);
  goToReferEarnPage();
};

export function openReferEarnInvitation() {
  if (!openingReferEarnInvitation) {
    openingReferEarnInvitation = openReferEarnInvitationFlow().finally(() => {
      openingReferEarnInvitation = undefined;
      globalStore.globalLoading.next(false);
    });
  }

  return openingReferEarnInvitation;
}

export function openReferEarnInvitationRoute(routeName?: string) {
  if (!isReferEarnInvitationRoute(routeName)) {
    return false;
  }

  openReferEarnInvitation();
  return true;
}
